import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Input, Modal as AntModal, Select } from "antd";
import PoaSignatureSelect from "../PoaSignatureSelect/PoaSignatureSelect";

const Field = styled.div`
  margin-top: 14px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
`;

const Help = styled.p`
  margin: 8px 0 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
`;

const ErrorText = styled.p`
  margin: 10px 0 0;
  color: #dc2626;
  font-size: 12px;
`;

const TemplateReview = styled.div`
  margin-top: 16px;
  display: grid;
  gap: 14px;
`;

const TemplateCard = styled.section`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  background: #fff;
`;

const TemplateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 10px;
`;

const TemplateTitle = styled.h4`
  margin: 0;
  color: #111827;
  font-size: 14px;
`;

const packageOptions = (packages) =>
  (packages || []).map((pkg) => ({
    value: String(pkg.id),
    label: `${pkg.name}${pkg.template_count != null ? ` (${pkg.template_count} templates)` : ""}`,
  }));

const folderOptions = (folders) => [
  { value: "", label: "Matter Root" },
  ...(folders || []).map((folder) => ({
    value: folder.value,
    label: folder.label,
  })),
];

export default function GeneratePackageFromMatterModal({
  open,
  packages,
  folders,
  matterId,
  loading,
  generating,
  previewing,
  onCancel,
  onPreview,
  onGenerate,
}) {
  const [packageId, setPackageId] = useState("");
  const [folderId, setFolderId] = useState("");
  const [poaAttorney, setPoaAttorney] = useState("");
  const [step, setStep] = useState("select");
  const [preview, setPreview] = useState(null);
  const [documentName, setDocumentName] = useState("");
  const [error, setError] = useState("");

  const pkgOptions = useMemo(() => packageOptions(packages), [packages]);
  const destOptions = useMemo(() => folderOptions(folders), [folders]);

  const reset = () => {
    setPackageId("");
    setFolderId("");
    setPoaAttorney("");
    setStep("select");
    setPreview(null);
    setDocumentName("");
    setError("");
  };

  const handleCancel = () => {
    if (generating) return;
    reset();
    onCancel();
  };

  const handleReview = async () => {
    if (!packageId) {
      setError("Select a package first.");
      return;
    }
    setError("");
    try {
      const data = await onPreview({
        packageId,
        folderId: folderId || null,
      });
      setPreview(data);
      const firstTemplate = (data?.templates || [])[0] || {};
      setDocumentName(firstTemplate.output_name || firstTemplate.template_name || "");
      setStep("review");
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Could not load fields for this package."
      );
    }
  };

  const handleGenerate = () => {
    if (step === "select") {
      handleReview();
      return;
    }
    setError("");
    onGenerate({
      packageId,
      folderId: folderId || null,
      fieldOverrides: {},
      documentName: documentName.trim(),
      poaAttorney: poaAttorney || null,
    });
  };

  const canEdit = step === "review";

  return (
    <AntModal
      title="Generate Package"
      open={open}
      onCancel={handleCancel}
      onOk={handleGenerate}
      okText={
        step === "select"
          ? (previewing ? "Loading..." : "Next")
          : (generating ? "Generating..." : "Generate documents")
      }
      okButtonProps={{ disabled: generating || loading || previewing }}
      cancelButtonProps={{ disabled: generating || previewing }}
      destroyOnClose
      afterClose={reset}
      width={620}
    >
      <Help>
        Generate all templates in a package directly into this Matter. DMS will use
        each template&apos;s saved mappings, conditional sections, and output names.
      </Help>

      <Field>
        <Label htmlFor="matter-package-select">Package</Label>
        <Select
          id="matter-package-select"
          data-testid="matter-package-select"
          showSearch
          value={packageId || undefined}
          loading={loading}
          placeholder={loading ? "Loading packages..." : "Select package"}
          options={pkgOptions}
          optionFilterProp="label"
          onChange={setPackageId}
          disabled={canEdit}
          style={{ width: "100%" }}
        />
      </Field>

      <Field>
        <Label htmlFor="matter-package-destination">Destination</Label>
        <Select
          id="matter-package-destination"
          data-testid="matter-package-destination"
          showSearch
          value={folderId}
          placeholder="Matter Root"
          options={destOptions}
          optionFilterProp="label"
          onChange={setFolderId}
          disabled={canEdit}
          style={{ width: "100%" }}
        />
        <Help>
          Pick Matter Root or an existing folder in this Matter. This does not use
          the Settings Generated Documents folder templates.
        </Help>
      </Field>

      <Field>
        <Label htmlFor="matter-package-poa">POA signature</Label>
        <PoaSignatureSelect
          labelId="matter-package-poa"
          matterId={matterId}
          value={poaAttorney}
          onChange={setPoaAttorney}
          disabled={generating}
        />
      </Field>

      {step === "review" && (
        <TemplateReview data-testid="package-name-review">
          <Help>
            Confirm the document name for this generation. You can rename it
            later from the document preview if needed.
          </Help>
          <TemplateCard>
            <TemplateHeader>
              <div>
                <TemplateTitle>
                  {preview?.package?.name || "Generated document"}
                </TemplateTitle>
              </div>
            </TemplateHeader>
            <Field>
              <Label htmlFor="package-document-name">Document name</Label>
              <Input
                id="package-document-name"
                data-testid="package-document-name"
                value={documentName}
                onChange={(event) => setDocumentName(event.target.value)}
              />
            </Field>
          </TemplateCard>
          {preview?.errors?.length > 0 && (
            <ErrorText>
              {preview.errors.length} template preview error
              {preview.errors.length !== 1 ? "s" : ""}. You can still generate
              the templates that loaded successfully.
            </ErrorText>
          )}
        </TemplateReview>
      )}

      {error && <ErrorText>{error}</ErrorText>}
    </AntModal>
  );
}

GeneratePackageFromMatterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  packages: PropTypes.array,
  folders: PropTypes.array,
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool,
  generating: PropTypes.bool,
  previewing: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
};
